// Imports
import Image from "next/image";
import { cmsImageUrl } from "@/lib/cmsImageUrl";
import { Placeholder } from "@/components/global/page-builder/Placeholder";
import { cn } from "@/lib/utils";

export function Img({
  id,
  className = "",
  data,
  sizes = "(max-width: 768px) 100vw, 50vw",
  fill = false,
  priority = false,
  style,
}) {
  if (!data) {
    return null;
  }

  // Override fill settings if the image asset has strictly defined to be contained.
  if (data.contain_image) {
    fill = false;
  }

  // Build a stable, versioned image URL
  const src = cmsImageUrl(data?.permalink ?? "", data?.last_modified, {
    width: data?.width,
    height: data?.height,
  });
  if (!src) {
    return (
      <Placeholder
        className={fill ? "absolute inset-0 aspect-square" : className}
        type="image-placeholder"
        isLarge={!fill}
      />
    );
  }
  const isSvg = /\.svg(\?|#|$)/i.test(src) || data?.extension === "svg";
  const width = Number(data?.width);
  const height = Number(data?.height);
  const hasIntrinsicDimensions =
    Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0;

  if (fill) {
    return (
      <Image
        id={id}
        src={src}
        sizes={sizes}
        fill={true}
        // Keep important images high priority without emitting a cross-origin
        // preload for the Cloudflare asset CDN. The CDN does not return CORS
        // headers, so browser preloads can fail even though the image loads.
        fetchPriority={priority ? "high" : undefined}
        loading={priority ? "eager" : undefined}
        unoptimized={isSvg}
        style={{ objectPosition: data?.focus_css, ...style }}
        alt={data?.alt ?? "Image"}
        className={cn("object-cover", className)}
      />
    );
  }

  // Statamic can return null dimensions while an asset is awaiting metadata
  // generation. next/image throws for that otherwise valid asset, so use the
  // browser's intrinsic sizing until the CMS supplies dimensions.
  if (!hasIntrinsicDimensions) {
    return (
      <img
        id={id}
        src={src}
        sizes={sizes}
        fetchPriority={priority ? "high" : undefined}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        alt={data?.alt ?? "Image"}
        style={style}
        className={cn("object-contain w-full h-full", className)}
      />
    );
  }

  return (
    <Image
      id={id}
      src={src}
      sizes={sizes}
      width={width}
      height={height}
      fetchPriority={priority ? "high" : undefined}
      loading={priority ? "eager" : undefined}
      unoptimized={isSvg}
      alt={data?.alt ?? "Image"}
      style={style}
      className={cn("object-contain w-full h-full", className)}
    />
  );
}
